import React, { Suspense, lazy, useEffect, useState, useRef, useMemo } from 'react'
import { TabNavigationProvider } from 'hooks/useTabNavigation'
import { BrowserRouter, Switch, Route, Redirect, useLocation, matchPath } from 'react-router-dom'
import { connect, useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import PublicRoute from 'components/Routes/PublicRoute'
import PublicClientRoute from 'components/Routes/PublicClientRoute'
import PublicDriverRoute from 'components/Routes/PublicDriverRoute'
import PrivateRoute from 'components/Routes/PrivateRoute'
import PrivateClientRoute from 'components/Routes/PrivateClientRoute'
import PrivateDriverRoute from 'components/Routes/PrivateDriverRoute'
import PublicPassengerRoute from 'components/Routes/PublicPassengerRoute'
import PrivatePassengerRoute from 'components/Routes/PrivatePassengerRoute'
import PrivateAssociateRoute from 'components/Routes/PrivateAssociateRoute'
import {
    loginWithToken,
    clientLoginWithToken,
    driverLoginWithToken,
    passengerLoginWithToken,
    setLanguage,
    supplierLoginWithToken,
    associateLoginWithToken,
} from 'store/actions/app'
import GlobalCommunications from '../components/GlobalCommunications'
import { UnifiedPrivateLayout } from 'components/TabMenuBar/UnifiedPrivateLayout'

import ContentOverlayLoader from 'components/ContentOverlayLoader'

import SupplierLogin from './login/SupplierLogin'
import PrivateSupplierRoute from 'components/Routes/PrivateSupplierRoute'
import PublicSupplierRoute from 'components/Routes/PublicSupplierRoute'
import WizardCatalog from './wizard/WizardCatalog'
import ModuleNotFound from './error/moduleNotFound'
import ClientUsers from './client-users/ClientUsers'
import TourManager from '../components/Tour/TourManager'
// import ClientGroupPassengersPage from './transporte de personal/ClientPassengerSite/ClientGroupPassengersPage'
import TitleSync from './../components/TitleSync'
import PartnerBillingPage from './superadmin/PartnerBillingPage'
import PartnerBillingIndividualPage from './superadmin/PartnerBillingIndividualPage'
import QueryExecutorPage from './superadmin/QueryExecutorPage'
import ChatWidget from './home/ChatWidget'

const HomePage = lazy(() => import('./home/HomePage'))
const LandingPage = lazy(() => import('./landing/LandingPage'))
const LoginRoute = lazy(() => import('./login/index'))
const RolePage = lazy(() => import('./settings/RolePage'))
const RoleIndividualPage = lazy(() => import('./settings/RoleIndividualPage'))
const SupplierDocumentsPage = lazy(() =>
    import('./transporte de personal/SupplierSite/SupplierDocumentsPage')
)
const ClientHomePage = lazy(() =>
    import(
        './transporte de personal/ClientPassengerSite/ClientHomePage'
    )
)
const ClientDocumentsPage = lazy(() =>
    import('./transporte de personal/ClientPassengerSite/ClientDocumentPage')
)
const ClientPassengersPage = lazy(() =>
    import('./transporte de personal/ClientPassengerSite/ClientPassengersPage')
)
const ClientPassengersIndividualPage = lazy(() =>
    import(
        './transporte de personal/ClientPassengerSite/ClientPassengersIndividualPage'
    )
)
const ClientPassengersReportPage = lazy(() =>
    import(
        './transporte de personal/ClientPassengerSite/ClientPassengersReportPage'
    )
)
const ClientDashboardPage = lazy(() =>
    import('./transporte de personal/ClientPassengerSite/ClientDashboardPage')
)
const ClientGroupsReportPage = lazy(() =>
    import('./transporte de personal/ClientPassengerSite/ClientGroupsReportPage')
)
const PassengersIndividualPage = lazy(() =>
    import('./transporte de personal/PassengerSite/PassengersIndividualPage')
)
const ClientGroupPassengersPage = lazy(() =>
    import(
        './transporte de personal/ClientPassengerSite/ClientGroupPassengersPage'
    )
)
const ClientShiftPassengersPage = lazy(() =>
    import(
        './transporte de personal/ClientPassengerSite/ClientShiftPassengersPage'
    )
)
const ClientSupervisorPassengersPage = lazy(() => import('./transporte de personal/ClientPassengerSite/ClientSupervisorPassengersPage'))
const ClientSupervisorPassengersIndividualPage = lazy(() => import('./transporte de personal/ClientPassengerSite/ClientSupervisorPassengersIndividualPage'))
const ClientAreaPassengersPage = lazy(() =>
    import(
        './transporte de personal/ClientPassengerSite/ClientAreaPassengersPage'
    )
)
const PassengerGroupsReportPage = lazy(() =>
    import(
        './transporte de personal/ClientPassengerGroupsReport'
    )
);
const ClientGroupPassengersIndividualPage = lazy(() =>
    import(
        './transporte de personal/ClientPassengerSite/ClientGroupPassengersIndividualPage'
    )
)
const ClientShiftPassengersIndividualPage = lazy(() =>
    import(
        './transporte de personal/ClientPassengerSite/ClientShiftPassengersIndividualPage'
    )
)
const ClientAreaPassengersIndividualPage = lazy(() =>
    import(
        './transporte de personal/ClientPassengerSite/ClientAreaPassengersIndividualPage'
    )
)
const DriversPage = lazy(() =>
    import('./transporte de personal/DriverSite/DriversIndividualPage')
)
const DeliveryRoutesReport = lazy(() =>
    import('./transporte de personal/DriverSite/DriverDeliveryRoutesReport')
)
const ClientVisits = lazy(() =>
    import('./transporte de mercancia/ClientVisitsIndividualPage')
)
const DriverDashboardPage = lazy(() =>
    import('./transporte de mercancia/DriverDashboardPage')
)
const ClientMobileRoutesReportPage = lazy(() =>
    import(
        './transporte de personal/ClientPassengerSite/ClientMobileRoutesReportPage'
    )
)
const ClientFormInstancePage = lazy(() =>
    import('./transporte de personal/ClientPassengerSite/ClientFormInstancePage')
)
const DriverFormInstancesPage = lazy(() =>
    import('./transporte de personal/DriverSite/DriverFormInstancesPage')
)
const DriverFormInstancePage = lazy(() =>
    import('./transporte de personal/DriverSite/DriverFormInstancePage')
)
const PassengerFormInstancePage = lazy(() =>
    import('./transporte de personal/PassengerSite/PassengerFormInstancePage')
)
const PassengerFormReportDetailPage = lazy(() =>
    import('./transporte de personal/PassengerSite/PassengerFormDetailPage')
)
const SupplierFormInstancePage = lazy(() =>
    import('./transporte de personal/SupplierSite/SupplierFormInstancePage.js')
)
const SupplierFormReportDetailPage = lazy(() =>
    import('./transporte de personal/SupplierSite/SupplierFormReportDetailPage')
)
const DriverInspectionPage = lazy(() => import('./driver/DriverInspectionPage'))
const SupplierHomePage = lazy(() => import(
    './transporte de personal/SupplierSite/SupplierHomePage'
))
const ClientFormReportDetailPage = lazy(() =>
    import('./transporte de personal/ClientPassengerSite/ClientFormDetailPage')
)
const ClientBinnaclePage = lazy(() =>
    import('./transporte de personal/ClientPassengerSite/ClientBinnaclePage')
)
const DriverFormReportDetailPage = lazy(() =>
    import('./transporte de personal/DriverSite/DriverFormDetailPage')
)
const RouteTrackingPage = lazy(() =>
    import('./transporte de personal/RouteTrackingPage')
)
const ClientRouteTrackingPage = lazy(() =>
    import('./transporte de personal/ClientRouteTrackingPage')
)
const ConsoleSuperAdmin = lazy(() => import('./login/ConsoleSuperAdmin'))
const ProcessCatalog = lazy(() =>
    import('../routes/process_catalog/ProcessCatalog')
)
const Procedure = lazy(() => import('../routes/wizard/WizardProcedure'))
const ProcedureIndiv = lazy(() =>
    import('../routes/wizard/WizardProcedureIndividual')
)
const FormatImpression = lazy(() =>
    import('../routes/wizard/WizardImpressionFormats')
)
const FormatImpressionIndiv = lazy(() =>
    import('../routes/wizard/WizardImpressionFormatsIndividual')
)
const ProcessIndiv = lazy(() =>
    import('../routes/process_catalog/ProcessCatalogIndividual')
)
const Wizard = lazy(() => import('../routes/wizard/WizardCatalog'))
const WizardIndiv = lazy(() =>
    import('../routes/wizard/WizardCatalogIndividual')
)
const Communication = lazy(() =>
    import('../routes/wizard/WizardCommunications')
)
const CommunicationIndiv = lazy(() => import('../routes/wizard/WizardCatalog'))
const AssociateLogin = lazy(() => import('./login/AssociateLogin'))
const AssociatesPage = lazy(() => import('./associates/AssociatesPage'))
const AssociatesIndividualPage = lazy(() =>
    import('./associates/AssociatesIndividualPage')
)
const ClientLogin = lazy(() => import('./login/ClientLogin'))
const DriverLogin = lazy(() => import('./login/DriverLogin'))
const PassengerLogin = lazy(() => import('./login/PassengerLogin'))
const PublicForm = lazy(() => import('../routes/form/PublicForm'))
const ModulesDashboard = lazy(() => import('./home/ModulesDashboardPage'))
const FuelSupplyTemplatePage = lazy(() => import('../routes/superadmin/FuelSupplyTemplatePage.js'))
const FuelSupplyTemplateImportScreen = lazy(() => import('../routes/superadmin/FuelSupplyTemplateImportScreen.js'))

// TEMPORAL: Página de Reportes de Solicitudes (para desarrollo/diseño)
const RequestsReportPage = lazy(() => import('./payroll/RequestsReportPage'))

// Reporte de Vacaciones (días tomados y pendientes por año de servicio)
const VacationReportPage = lazy(() => import('./payroll/VacationReportPage'))

// Páginas de Aprobación de Solicitudes
const RequestApprovalsPage = lazy(() => import('./payroll/RequestApprovalsPage'))

// Páginas de Vacaciones
const VacationRequestList = lazy(() => import('./payroll/VacationRequestList'))
const ExtraHourRequests = lazy(() => import('./payroll/ExtraHourRequests'))

const normalizeModuleUrl = (url) => {
    if (!url || typeof url !== 'string') return ''
    return `/${url.replace(/^\/+|\/+$/g, '')}`
}

const isInlineModuleUrl = (normalizedUrl) => {
    if (!normalizedUrl) return false
    const lower = normalizedUrl.toLowerCase()
    return (
        lower.endsWith('/new') ||
        lower.endsWith('/nuevo') ||
        /\/:[^/]+$/.test(lower)
    )
}

const getInlineParentUrl = (inlineUrl) => {
    if (!inlineUrl) return ''
    return inlineUrl.replace(/\/(new|nuevo|:[^/]+)$/, '') || inlineUrl
}



// Gestor de Ubicaciones con Geofencing
const LocationManager = lazy(() => import('./payroll/LocationManager'))
const TemporaryExitReasonsPage = lazy(() => import('./payroll/TemporaryExitReasonsPage'))




const Routers = (props) => {
    const { t, i18n, ready } = useTranslation('translation', { useSuspense: false });
    const dispatch = useDispatch();
    const impersonate_mode = useSelector(state => state.app.impersonate_mode);
    const [isTryingToLogin, setIsTryingToLogin] = useState(true)
    const [showChatWidget, setShowChatWidget] = useState(false)

    const modulesMeta = useMemo(() => {
        const modules = (props.user?.modules ?? []).map((mod) => ({
            ...mod,
            routePath: normalizeModuleUrl(mod.url),
        }))

        const parentModules = new Map()
        const inlineEntries = []

        modules.forEach((mod) => {
            if (!mod.routePath || mod.is_tab) return

            if (isInlineModuleUrl(mod.routePath)) {
                inlineEntries.push({
                    inlinePath: mod.routePath,
                    parentPath: getInlineParentUrl(mod.routePath),
                })
                return
            }

            if (mod.permisitions?.ACCESS) {
                parentModules.set(mod.routePath, mod)
            }
        })

        const buildComponent = (module) => lazy(() => {
            if (typeof module.folder === 'string' && module.folder.trim() !== '') {
                return import(`${module.folder}`).catch((e) => {
                    console.warn(
                        `No se pudo cargar el módulo en ${module.folder}. Cargando la página de error.`
                    )
                    return import('./error/moduleNotFound')
                })
            }

            console.warn(
                `Ruta de módulo inválida o vacía: ${module.folder}. Cargando la página de error.`
            )
            return import('./error/moduleNotFound')
        })

        const modulesToRender = Array.from(parentModules.values())
            .sort((a, b) => b.routePath.length - a.routePath.length)
            .map((mod) => ({
                ...mod,
                ComponentPage: buildComponent(mod),
            }))

        const inlineRoutes = inlineEntries
            .map((entry) => {
                const parentModule = modulesToRender.find(
                    (mod) => mod.routePath === entry.parentPath
                )
                if (!parentModule) return null
                return {
                    path: entry.inlinePath,
                    ComponentPage: parentModule.ComponentPage,
                    title: parentModule.name,
                }
            })
            .filter(Boolean)

        const inlinePathSet = new Set(inlineRoutes.map((route) => route.path))
        const fallbackInlineSuffixes = ['new', 'nuevo']
        modulesToRender.forEach((module) => {
            const base = module.routePath.replace(/\/$/, '')
            fallbackInlineSuffixes.forEach((suffix) => {
                const path = `${base}/${suffix}`
                if (inlinePathSet.has(path)) return
                inlinePathSet.add(path)
                inlineRoutes.push({
                    path,
                    ComponentPage: module.ComponentPage,
                    title: module.name,
                })
            })
        })

        return { modulesToRender, inlineRoutes }
    }, [props.user?.modules])
    const { modulesToRender, inlineRoutes } = modulesMeta

    // --- BROADCAST CHANNEL LISTENER ---
    // Handles impersonation lifecycle between superadmin and impersonated tabs.
    // Uses a delayed cleanup to distinguish between tab close vs reload:
    //   - beforeunload sends "impersonation_closed" (fires on both close & reload)
    //   - On mount, impersonated tabs send "impersonation_alive" (only fires on reload)
    //   - Superadmin waits 3s after "closed" — if "alive" arrives, cancel cleanup
    useEffect(() => {
        let channel;
        let cleanupTimer = null;
        try {
            channel = new BroadcastChannel("impersonation_channel");
            channel.onmessage = (event) => {
                // --- Impersonated tab: close when superadmin requests ---
                if (event.data?.type === "close_impersonation") {
                    const isImpersonatedTab =
                        sessionStorage.getItem("@impersonated") === "1" ||
                        localStorage.getItem("@impersonated") === "1";
                    if (isImpersonatedTab) {
                        console.log("[BroadcastChannel] Cerrando pestaña impersonada...");
                        localStorage.removeItem("@impersonated");
                        localStorage.removeItem("@roleImpersonated");
                        localStorage.removeItem("@impersonate_role");
                        localStorage.removeItem("@client_token");
                        localStorage.removeItem("@driver_token");
                        localStorage.removeItem("@supplier_token");
                        localStorage.removeItem("@passenger_token");
                        sessionStorage.removeItem("active_role");
                        window.close();
                        setTimeout(() => {
                            window.location.href = "/login";
                        }, 500);
                    }
                }

                // --- Superadmin tab: delayed cleanup on impersonated tab unload ---
                if (event.data?.type === "impersonation_closed") {
                    console.log("[BroadcastChannel] impersonation_closed recibido, esperando 3s...");
                    // Wait before cleaning — if the tab is just reloading,
                    // it will send "impersonation_alive" and cancel this.
                    if (cleanupTimer) clearTimeout(cleanupTimer);
                    cleanupTimer = setTimeout(() => {
                        console.log("[BroadcastChannel] Limpiando bridge y @roleImpersonated (tab cerrada)");
                        localStorage.removeItem("@roleImpersonated");
                        localStorage.removeItem("@imp_bridge");
                        cleanupTimer = null;
                    }, 3000);
                }

                // --- Superadmin tab: cancel cleanup if impersonated tab is still alive ---
                if (event.data?.type === "impersonation_alive") {
                    if (cleanupTimer) {
                        console.log("[BroadcastChannel] impersonation_alive recibido, cancelando limpieza");
                        clearTimeout(cleanupTimer);
                        cleanupTimer = null;
                    }
                }
            };

            // Notify superadmin on unload (fires on both close and reload)
            const handleBeforeUnload = () => {
                const isImpersonated =
                    sessionStorage.getItem("@impersonated") === "1" ||
                    localStorage.getItem("@impersonated") === "1" ||
                    new URLSearchParams(window.location.search).get("impersonated") === "1";
                if (isImpersonated) {
                    try {
                        const ch = new BroadcastChannel("impersonation_channel");
                        ch.postMessage({ type: "impersonation_closed" });
                        ch.close();
                    } catch (e) { /* ignore */ }
                }
            };
            window.addEventListener("beforeunload", handleBeforeUnload);

            // [SUPERADMIN RELOAD GUARD]
            // If the superadmin tab is being refreshed while an impersonated session is active,
            // warn them and close the impersonated tab cleanly so the reload works correctly.
            const handleSuperadminBeforeUnload = (e) => {
                const isThisTabImpersonated =
                    sessionStorage.getItem("@impersonated") === "1" ||
                    localStorage.getItem("@impersonated") === "1";
                const bridgeRaw = localStorage.getItem("@imp_bridge");
                const bridge = bridgeRaw ? (() => { try { return JSON.parse(bridgeRaw); } catch { return null; } })() : null;

                // Bridge must exist, be fresh (< 24h), and this tab must NOT be the impersonated one
                const BRIDGE_MAX_AGE = 24 * 60 * 60 * 1000;
                const bridgeIsActive = bridge && bridge.ts && (Date.now() - bridge.ts) < BRIDGE_MAX_AGE;

                if (!isThisTabImpersonated && bridgeIsActive) {
                    // Show native browser confirmation dialog
                    e.preventDefault();
                    e.returnValue = "Al continuar se cerrarán todas las pestañas con sesiones de otros usuarios abiertas.";

                    // Clean the bridge so the superadmin tab reloads cleanly (no Path B interference)
                    localStorage.removeItem("@imp_bridge");

                    // Tell the impersonated tab to close itself
                    try {
                        const ch = new BroadcastChannel("impersonation_channel");
                        ch.postMessage({ type: "close_impersonation" });
                        ch.close();
                    } catch (e) { /* ignore */ }
                }
            };
            window.addEventListener("beforeunload", handleSuperadminBeforeUnload);

            // If this tab is impersonated, announce it's alive (cancels cleanup on reload)
            const isImpersonated =
                sessionStorage.getItem("@impersonated") === "1" ||
                localStorage.getItem("@impersonated") === "1" ||
                new URLSearchParams(window.location.search).get("impersonated") === "1";
            if (isImpersonated) {
                try {
                    const ch = new BroadcastChannel("impersonation_channel");
                    ch.postMessage({ type: "impersonation_alive" });
                    ch.close();
                } catch (e) { /* ignore */ }
            }

            return () => {
                window.removeEventListener("beforeunload", handleBeforeUnload);
                window.removeEventListener("beforeunload", handleSuperadminBeforeUnload);
                if (cleanupTimer) clearTimeout(cleanupTimer);
                if (channel) {
                    try { channel.close(); } catch (e) { /* ignore */ }
                }
            };
        } catch (e) {
            console.warn("[Routers] BroadcastChannel not supported:", e);
        }
        return () => {
            if (cleanupTimer) clearTimeout(cleanupTimer);
            if (channel) {
                try { channel.close(); } catch (e) { /* ignore */ }
            }
        };
    }, []);

    const client = useSelector(state => state.app.client)
    const user = useSelector(state => state.app.user)
    const driver = useSelector(state => state.app.driver)
    const supplier = useSelector(state => state.app.supplier)

    const safeTry = async (fn, params, keysToClear = [], nonUnique = false) => {
        try {
            let finalParams = params;

            if (params && typeof params === 'object') {
                finalParams = { ...params, nonUnique };
            }

            await fn(finalParams);
        } catch (error) {
            keysToClear.forEach(k => {
                localStorage.removeItem(k);
                sessionStorage.removeItem(k);
            });
            throw error;
        }
    };

    // Throttle para prevenir múltiples llamadas a tryToLogin
    const lastLoginAttemptRef = useRef(0);
    const LOGIN_THROTTLE_MS = 2000; // 2 segundos entre intentos

    // [IMPERSONATED SESSION BRIDGE] — Write on active impersonation
    // Writes the bridge to localStorage when impersonate_mode becomes active.
    // Bridge removal is handled exclusively by cleanImpersonationTokens() in useImpersonate.js
    // to avoid Tab 1 (SuperAdmin, impersonate_mode=false) clearing Tab 2's bridge.
    useEffect(() => {
        if (!impersonate_mode) return;

        const activeRole = sessionStorage.getItem('active_role');
        const roleTokenMap = {
            client: '@client_token',
            driver: '@driver_token',
            passenger: '@passenger_token',
            supplier: '@supplier_token',
            user: '@token',
        };
        const tokenKey = activeRole ? roleTokenMap[activeRole] : null;
        const token = tokenKey ? sessionStorage.getItem(tokenKey) : null;

        if (token && activeRole) {
            const bridge = { token, role: activeRole, ts: Date.now() };
            localStorage.setItem('@imp_bridge', JSON.stringify(bridge));
            console.log('[ImpersonationBridge] Bridge written from useEffect, role:', activeRole, '✓');
        }
    }, [impersonate_mode]);

    const tryToLogin = async () => {
        const now = Date.now();
        const timeSinceLastAttempt = now - lastLoginAttemptRef.current;

        // Helper: check sessionStorage first (for impersonated tabs), then localStorage
        const getToken = (key) => sessionStorage.getItem(key) || localStorage.getItem(key);

        const searchParams = new URLSearchParams(window.location.search);
        const impersonatedFromUrl = searchParams.get('impersonated') === '1';
        const accessTokenFromUrl = searchParams.get('access_token');
        const roleFromUrl = searchParams.get('role');

        // [IMPERSONATED SUB-TAB LOGIN]
        // Two paths for new impersonated tabs:
        //   A) URL-based: original useImpersonate opens /login?access_token=...&role=...&impersonated=1&redirect=...
        //   B) Bridge-based: any new tab (opened any way) reads @imp_bridge from localStorage
        //      when sessionStorage has no tokens yet, indicating a fresh tab in an impersonated context.

        const hasSessionToken = !!(
            sessionStorage.getItem('@token') ||
            sessionStorage.getItem('@client_token') ||
            sessionStorage.getItem('@driver_token') ||
            sessionStorage.getItem('@passenger_token') ||
            sessionStorage.getItem('@supplier_token')
        );

        // === DIAGNOSTIC — remove after debugging ===
        const _bridge = localStorage.getItem('@imp_bridge');
        console.log('[tryToLogin] STATE CHECK', {
            url: window.location.href,
            hasSessionToken,
            impersonatedFromUrl,
            accessTokenFromUrl: !!accessTokenFromUrl,
            roleFromUrl,
            bridge: _bridge ? JSON.parse(_bridge) : null,
            sessionImpersonated: sessionStorage.getItem('@impersonated'),
            activeRole: sessionStorage.getItem('active_role'),
        });
        // ==========================================

        // Path A: URL-based (useImpersonate original flow)
        if (impersonatedFromUrl && accessTokenFromUrl && roleFromUrl && !hasSessionToken) {
            console.log('[tryToLogin] → Path A: URL token, role:', roleFromUrl);
            sessionStorage.setItem('@impersonated', '1');
            localStorage.setItem('@impersonated', '1');
            sessionStorage.setItem('active_role', roleFromUrl);

            const roleActionMap = {
                client: { action: props.clientLoginWithToken, paramKey: 'client_token' },
                driver: { action: props.driverLoginWithToken, paramKey: 'driver_token' },
                passenger: { action: props.passengerLoginWithToken, paramKey: 'passenger_token' },
                supplier: { action: props.supplierLoginWithToken, paramKey: 'supplier_token' },
                user: { action: props.loginWithToken, paramKey: 'token' },
            };
            const roleConfig = roleActionMap[roleFromUrl];
            if (roleConfig) {
                try {
                    await roleConfig.action({ [roleConfig.paramKey]: accessTokenFromUrl, nonUnique: true });
                    localStorage.removeItem('@impersonated');
                    dispatch({ type: 'SET_IMPERSONATE_MODE', payload: true });
                    const redirectParam = searchParams.get('redirect');
                    const destination = redirectParam ? decodeURIComponent(redirectParam) : '/';
                    window.location.replace(destination);
                } catch (e) {
                    console.error('[tryToLogin] Path A: Auth failed:', e);
                    localStorage.removeItem('@impersonated');
                    setIsTryingToLogin(false);
                }
                return;
            }
        }

        // Path B: Bridge-based (any new tab opened from an impersonated session)
        // Guards: no session tokens, no URL token, AND not a login page.
        // The login-page guard prevents Tab 1 (SuperAdmin at /login_superadmin) from being
        // accidentally authenticated as the impersonated user via the shared @imp_bridge.
        const isLoginPage = window.location.pathname.includes('/login');
        if (!hasSessionToken && !accessTokenFromUrl && !isLoginPage) {
            try {
                const bridgeRaw = localStorage.getItem('@imp_bridge');
                console.log('[tryToLogin] → Path B check, bridge exists:', !!bridgeRaw);
                if (bridgeRaw) {
                    const bridge = JSON.parse(bridgeRaw);
                    const AGE_LIMIT_MS = 8 * 60 * 60 * 1000; // 8 hours
                    const age = Date.now() - (bridge.ts || 0);
                    const isRecent = age < AGE_LIMIT_MS;
                    console.log('[tryToLogin] → Path B bridge:', { role: bridge.role, hasToken: !!bridge.token, ageSeconds: Math.round(age / 1000), isRecent });
                    if (isRecent && bridge.token && bridge.role) {
                        sessionStorage.setItem('@impersonated', '1');
                        localStorage.setItem('@impersonated', '1');
                        sessionStorage.setItem('active_role', bridge.role);

                        const roleActionMap = {
                            client: { action: props.clientLoginWithToken, paramKey: 'client_token' },
                            driver: { action: props.driverLoginWithToken, paramKey: 'driver_token' },
                            passenger: { action: props.passengerLoginWithToken, paramKey: 'passenger_token' },
                            supplier: { action: props.supplierLoginWithToken, paramKey: 'supplier_token' },
                            user: { action: props.loginWithToken, paramKey: 'token' },
                        };
                        const roleConfig = roleActionMap[bridge.role];
                        if (roleConfig) {
                            try {
                                await roleConfig.action({ [roleConfig.paramKey]: bridge.token, nonUnique: true });
                                localStorage.removeItem('@impersonated');
                                dispatch({ type: 'SET_IMPERSONATE_MODE', payload: true });
                                console.log('[tryToLogin] → Path B: SUCCESS');
                            } catch (e) {
                                console.error('[tryToLogin] → Path B: Auth FAILED:', e);
                                localStorage.removeItem('@impersonated');
                            } finally {
                                setIsTryingToLogin(false);
                            }
                            return;
                        }
                    }
                }
            } catch (e) {
                console.warn('[tryToLogin] Path B: Bridge parse error:', e);
            }
        }

        // Verificar si hay tokens en localStorage OR sessionStorage
        const hasTokens = getToken('@token') ||
            getToken('@client_token') ||
            getToken('@passenger_token') ||
            getToken('@driver_token') ||
            getToken('@supplier_token') ||
            getToken('@super_admin_token');

        // Solo aplicar throttling si hay tokens Y no ha pasado suficiente tiempo
        // Si no hay tokens (después de 401), permitir login inmediato
        if (hasTokens && timeSinceLastAttempt < LOGIN_THROTTLE_MS) {
            console.warn(`⚠️ Login throttled. Esperando ${LOGIN_THROTTLE_MS - timeSinceLastAttempt}ms`);
            console.log('[tryToLogin] DIAG throttled — isTryingToLogin queda en true (return sin reset)');
            return;
        }

        lastLoginAttemptRef.current = now;

        const impersonatedFromStorage =
            sessionStorage.getItem('@impersonated') === '1' ||
            localStorage.getItem('@impersonated') === '1';

        const isImpersonated = impersonatedFromUrl || impersonatedFromStorage;

        // [LOGIN PAGE + IMPERSONATION GUARD]
        // If the user explicitly navigates to a login page while having an impersonated session
        // (e.g., navigating to /login_superadmin from within an impersonated client tab),
        // clear the impersonation state so they see a clean login form, not the impersonated session.
        if (isLoginPage && impersonatedFromStorage) {
            sessionStorage.removeItem('@impersonated');
            sessionStorage.removeItem('active_role');
            localStorage.removeItem('@impersonated');
            dispatch({ type: 'SET_IMPERSONATE_MODE', payload: false });
            setIsTryingToLogin(false);
            return;
        }

        if (impersonatedFromUrl && !impersonatedFromStorage) {
            // Store in sessionStorage for per-tab isolation (auto-cleared on tab close)
            sessionStorage.setItem('@impersonated', '1');
            // Also keep in localStorage temporarily for setUniqueSessionToken detection
            localStorage.setItem('@impersonated', '1');
        }

        dispatch({ type: 'SET_IMPERSONATE_MODE', payload: isImpersonated });

        // [SMART SESSION MANAGEMENT]
        const roleParam = searchParams.get('role');
        if (roleParam) {
            sessionStorage.setItem('active_role', roleParam);
        }
        const activeRole = sessionStorage.getItem('active_role');

        // [BRIDGE WRITE] Write imp_bridge directly when an impersonated session is active.
        // This runs synchronously before any async tasks, ensuring the bridge exists before
        // any new tab opens. This is the primary bridge-writing point.
        if (isImpersonated && activeRole) {
            const roleTokenMap = {
                client: '@client_token',
                driver: '@driver_token',
                passenger: '@passenger_token',
                supplier: '@supplier_token',
                user: '@token',
            };
            const tokenKey = roleTokenMap[activeRole];
            const bridgeToken = tokenKey ? (sessionStorage.getItem(tokenKey) || localStorage.getItem(tokenKey)) : null;
            if (bridgeToken) {
                const bridge = { token: bridgeToken, role: activeRole, ts: Date.now() };
                localStorage.setItem('@imp_bridge', JSON.stringify(bridge));
                console.log('[ImpersonationBridge] Bridge written in tryToLogin, role:', activeRole, '✓');
            } else {
                console.warn('[ImpersonationBridge] isImpersonated but no token found for role:', activeRole);
            }
        }

        const tasks = [];

        // Define token checks mapping
        // If activeRole is set, we ONLY check that specific token.
        // If NO activeRole is set (e.g. standard SuperAdmin), we fallback to checking standard tokens.

        const checks = [
            {
                role: 'user',
                tokenKey: '@token',
                check: () => {
                    const token = getToken('@token');
                    const employee_code = getToken('@employee_code');
                    if (token) {
                        if (employee_code) {
                            return safeTry(props.loginWithToken, { token, employee_code }, ['@token', '@employee_code'], isImpersonated);
                        } else {
                            return safeTry(props.loginWithToken, { token }, ['@token'], isImpersonated);
                        }
                    }
                    return null;
                }
            },
            { role: 'client', tokenKey: '@client_token', action: props.clientLoginWithToken, paramKey: 'client_token' },
            { role: 'passenger', tokenKey: '@passenger_token', action: props.passengerLoginWithToken, paramKey: 'passenger_token' },
            { role: 'driver', tokenKey: '@driver_token', action: props.driverLoginWithToken, paramKey: 'driver_token' },
            { role: 'supplier', tokenKey: '@supplier_token', action: props.supplierLoginWithToken, paramKey: 'supplier_token' },
            { role: 'super_admin', tokenKey: '@super_admin_token', action: props.associateLoginWithToken, isDirectToken: true }
        ];

        checks.forEach(c => {
            // Should we check this role's token?
            // - If activeRole is set: only check the matching role (+ super_admin for user)
            // - If activeRole is null + isImpersonated: check all (backwards compat)
            // - If activeRole is null + NOT impersonated: ONLY check user & super_admin.
            //   Role-specific tokens (client/driver/etc) are impersonation-only and should
            //   not auto-authenticate a non-impersonated tab (prevents stale token redirect).
            const isRoleSpecific = ['client', 'passenger', 'driver', 'supplier'].includes(c.role);
            const shouldCheck = activeRole
                ? (activeRole === c.role) || (activeRole === 'user' && (c.role === 'user' || c.role === 'super_admin'))
                : isImpersonated
                    ? true                              // impersonated + no activeRole: check all
                    : !isRoleSpecific;                  // not impersonated: skip role-specific tokens

            if (shouldCheck) {
                if (c.check) {
                    const task = c.check();
                    if (task) tasks.push(task);
                } else {
                    const tokenVal = getToken(c.tokenKey);
                    if (tokenVal) {
                        if (c.isDirectToken) {
                            tasks.push(safeTry(c.action, tokenVal, [c.tokenKey], isImpersonated));
                        } else {
                            tasks.push(safeTry(c.action, { [c.paramKey]: tokenVal }, [c.tokenKey], isImpersonated));
                        }
                    }
                }
            }
        });


        console.log('[tryToLogin] DIAG antes de allSettled — tasks.length:', tasks.length, 'hasTokens:', hasTokens, 'activeRole:', activeRole, 'isImpersonated:', isImpersonated);

        const results = await Promise.allSettled(tasks);

        console.log('[tryToLogin] DIAG después de allSettled —', results.map(r => ({ status: r.status, reason: r.status === 'rejected' ? (r.reason?.message || r.reason?.name) : undefined })));

        // After tokens are stored in sessionStorage, remove @impersonated from localStorage
        // so it doesn't leak to other tabs. The sessionStorage copy persists for this tab.
        if (isImpersonated) {
            localStorage.removeItem('@impersonated');
        }

        const anySuccess = results.some(r => r.status === 'fulfilled');
        const anyAttempted = results.length > 0;

        // Detect ChunkLoadError among failed results (stale chunks after new build)
        const hasChunkError = results.some(r =>
            r.status === 'rejected' && r.reason?.name === 'ChunkLoadError'
        );
        if (hasChunkError) {
            console.warn('[tryToLogin] ChunkLoadError detected — forcing hard reload to clear stale cache.');
            window.location.reload(true);
            return;
        }

        if (anyAttempted && !anySuccess) {
            const anyKicked = results.some(r =>
                r.status === 'rejected' && r.reason?.body?.session_kicked === true
            );
            const message = anyKicked
                ? 'La sesión se desconectó por inicio de sesión en otro dispositivo. Por favor, inicie sesión nuevamente.'
                : 'Tu sesión ha expirado.';

            localStorage.setItem("@flash", JSON.stringify({
                type: 'error',
                text: ready ? t(message) : message
            }));

            // Redirect to /login (not /) to avoid infinite loop:
            // redirecting to / would re-attempt auth, fail again, and loop.
            if (!window.location.pathname.includes('/login')) {
                window.location.replace('/login');
                return;
            } else {
                console.log("[tryToLogin] Session expired but staying on login page.");
            }
        }

        setIsTryingToLogin(false)
        console.log('[tryToLogin] DIAG setIsTryingToLogin(false) ejecutado')
    }
    const getLanguage = () => {
        if (!props.lang) {
            const lang = localStorage.getItem('@language') ?? 'es'
            if (lang) {
                props.setLanguage(lang, false)
                i18n.changeLanguage(lang)
            }
        }
    }
    useEffect(() => {
        tryToLogin()
        getLanguage()
    }, [])

    useEffect(() => {
        if ((user || client || driver || supplier) && !isTryingToLogin) {
            const timer = setTimeout(() => {
                setShowChatWidget(true)
            }, 700)
            return () => clearTimeout(timer)
        } else {
            setShowChatWidget(false)
        }
    }, [isTryingToLogin, user, client, driver, supplier])


    const loadingText = ready ? t('Cargando...') : 'Cargando...'

    if (isTryingToLogin) {
        return (
            <ContentOverlayLoader active={true} text={loadingText} />
        )
    }


    return (
        <React.Fragment>
            <BrowserRouter>

                <TitleSync modulesToRender={modulesToRender} flags={{ isAuthenticated: props.isAuthenticated }} />
                <GlobalCommunications
                    user={props.user}
                    client={props.client}
                    driver={props.driver}
                    supplier={props.supplier}
                />
                <TabNavigationProvider>
                    <UnifiedPrivateLayout />
                <Suspense fallback={
                    <ContentOverlayLoader active={true} text={loadingText} />
                }>
                        <Switch>
                            <Route
                                path='/landing'
                                component={LandingPage}
                                exact={true}
                            />
                            <PublicRoute
                                path='/login_superadmin'
                                component={AssociateLogin}
                                exact={true}
                            />
                            <PublicClientRoute
                                path='/login_client'
                                component={ClientLogin}
                                exact={true}
                            />
                            <PublicPassengerRoute
                                path='/login_passenger'
                                component={PassengerLogin}
                                exact={true}
                            />
                            <PublicDriverRoute
                                path='/login_driver'
                                component={DriverLogin}
                                exact={true}
                            />
                            <PublicSupplierRoute
                                path='/login_supplier'
                                component={SupplierLogin}
                                exact={true}
                            />
                            <PublicRoute
                                path='/login_superadmin'
                                component={ConsoleSuperAdmin}
                                exact={true}
                            />
                            <PublicClientRoute
                                path='/login_client'
                                component={ClientLogin}
                                exact={true}
                            />
                            <PublicPassengerRoute
                                path='/login_passenger'
                                component={PassengerLogin}
                                exact={true}
                            />
                            <PublicDriverRoute
                                path='/login_driver'
                                component={DriverLogin}
                                exact={true}
                            />
                            <PublicDriverRoute
                                path='/public_forms'
                                component={PublicForm}
                                exact={true}
                            />
                            <PublicDriverRoute
                                path='/public_forms/:id'
                                component={PublicForm}
                                exact={true}
                            />
                            <PublicRoute path='/login' component={LoginRoute} exact={true} />
                            <PrivateRoute path='/' component={HomePage} exact={true} />
                            <PrivateRoute
                                path='/settings/role'
                                component={RolePage}
                                exact={true}
                            />
                            <PrivateRoute
                                path='/settings/role/:id'
                                component={RoleIndividualPage}
                                exact={true}
                            />

                            {/* RUTA TEMPORAL: Para ver el diseño de Reportes de Solicitudes sin BD */}
                            <PrivateRoute
                                path='/requests_report_dev'
                                component={RequestsReportPage}
                                exact={true}
                            />

                            {/* Reporte de Vacaciones */}
                            <PrivateRoute
                                path='/vacation_report'
                                component={VacationReportPage}
                                exact={true}
                            />

                            {/* Rutas de Aprobación de Solicitudes */}
                            <PrivateRoute
                                path='/requests/approvals'
                                component={RequestApprovalsPage}
                                exact={true}
                            />

                            {/* Rutas de Solicitudes de Vacaciones */}
                            <PrivateRoute
                                path='/vacation_request'
                                component={VacationRequestList}
                                exact={true}
                            />
                            <PrivateRoute
                                path='/vacation_request_list'
                                component={VacationRequestList}
                                exact={true}
                            />
                            <PrivateRoute
                                path='/extra_hour_request'
                                component={ExtraHourRequests}
                                exact={true}
                            />
                            <PrivateRoute
                                path='/extra_hour_request/new'
                                component={ExtraHourRequests}
                                exact={true}
                            />
                            {/* Gestor de Ubicaciones */}
                            <PrivateRoute
                                path='/location_manager'
                                component={LocationManager}
                                exact={true}
                            />

                            {props.isSupplierAuthenticated && (
                                <>
                                    <PrivateSupplierRoute
                                        path='/supplier/supplier_dashboard'
                                        component={SupplierHomePage}
                                        exact={true}
                                    />
                                    <PrivateSupplierRoute
                                        path='/supplier/form_instance'
                                        component={SupplierFormInstancePage}
                                        exact={true}
                                    />
                                    <PrivateSupplierRoute
                                        path='/supplier/form_instance_report_detail'
                                        component={SupplierFormReportDetailPage}
                                        exact={true}
                                    />
                                    <PrivateSupplierRoute
                                        path='/supplier/supplier_documents'
                                        component={SupplierDocumentsPage}
                                        exact={true}
                                    />
                                </>
                            )}

                            {props.isClientAuthenticated && (
                                <>
                                    <PrivateClientRoute
                                        path='/client/passengers'
                                        component={ClientPassengersPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/passengers/:id'
                                        component={ClientPassengersIndividualPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/passengers_report'
                                        component={ClientPassengersReportPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/client_dashboard'
                                        component={ClientHomePage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/groups_report'
                                        component={ClientGroupsReportPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/client_group_passengers'
                                        component={ClientGroupPassengersPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/client_shift_passengers'
                                        component={ClientShiftPassengersPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute path='/client/client_supervisor_passengers' component={ClientSupervisorPassengersPage} exact={true} />
                                    <PrivateClientRoute path='/client/client_supervisor_passengers/:id' component={ClientSupervisorPassengersIndividualPage} exact={true} />
                                    <PrivateClientRoute
                                        path='/client/client_area_passengers'
                                        component={ClientAreaPassengersPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/client_group_passengers/:id'
                                        component={ClientGroupPassengersIndividualPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/client_shift_passengers/:id'
                                        component={ClientShiftPassengersIndividualPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/client_area_passengers/:id'
                                        component={ClientAreaPassengersIndividualPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/mobile_routes_report'
                                        component={ClientMobileRoutesReportPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/form_instance'
                                        component={ClientFormInstancePage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/form_instance_report_detail'
                                        component={ClientFormReportDetailPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/client_documents'
                                        component={ClientDocumentsPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/passenger_groups_report'
                                        component={PassengerGroupsReportPage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/user'
                                        component={ClientUsers}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/binnacle'
                                        component={ClientBinnaclePage}
                                        exact={true}
                                    />
                                    <PrivateClientRoute
                                        path='/client/client_route_tracking'
                                        component={ClientRouteTrackingPage}
                                        exact={true}
                                    />
                                </>
                            )}

                            {/* Gestor de Ubicaciones */}
                            <PrivateRoute
                                path='/location_manager'
                                component={LocationManager}
                                exact={true}
                            />
            <PrivateRoute
                path='/temporary_exit_reasons'
                component={TemporaryExitReasonsPage}
                exact={true}
            />
            <PrivateRoute
                path='/route_tracking'
                component={RouteTrackingPage}
                exact={true}
            />

                            {props.isPassengerAuthenticated && (
                                <>
                                    <PrivatePassengerRoute
                                        path='/passenger/passengers/'
                                        component={PassengersIndividualPage}
                                        exact={true}
                                    />

                                    <PrivatePassengerRoute
                                        path='/passenger/form_instance/'
                                        component={PassengerFormInstancePage}
                                        exact={true}
                                    />

                                    <PrivatePassengerRoute
                                        path='/passenger/form_instance_report_detail'
                                        component={PassengerFormReportDetailPage}
                                        exact={true}
                                    />
                                </>
                            )}

                            {props.isDriverAuthenticated && (
                                <>
                                    <PrivateDriverRoute
                                        path='/driver/drivers'
                                        component={DriversPage}
                                        exact={true}
                                    />
                                    <PrivateDriverRoute
                                        path='/driver/delivery_route_report'
                                        component={DeliveryRoutesReport}
                                        exact={true}
                                    />
                                    <PrivateDriverRoute
                                        path='/driver/client_visit'
                                        component={ClientVisits}
                                        exact={true}
                                    />
                                    <PrivateDriverRoute
                                        path='/driver/driver_dashboard'
                                        component={DriverDashboardPage}
                                        exact={true}
                                    />
                                    <PrivateDriverRoute
                                        path='/driver/driver_inspection_page'
                                        component={DriverInspectionPage}
                                        exact={true}
                                    />
                                    <PrivateDriverRoute
                                        path='/driver/form_instances'
                                        component={DriverFormInstancesPage}
                                        exact={true}
                                    />
                                    <PrivateDriverRoute
                                        path='/driver/form_instances/:id'
                                        component={DriverFormInstancePage}
                                        exact={true}
                                    />
                                    <PrivateDriverRoute
                                        path='/driver/create_form_instance'
                                        component={DriverFormInstancePage}
                                        exact={true}
                                    />

                                    <PrivateDriverRoute
                                        path='/driver/form_instance_report_detail'
                                        component={DriverFormReportDetailPage}
                                        exact={true}
                                    />
                                    <PrivateDriverRoute
                                        path='/driver/vacation_request'
                                        component={VacationRequestList}
                                        exact={true}
                                    />
                                    <PrivateDriverRoute
                                        path='/driver/vacation_request_list'
                                        component={VacationRequestList}
                                        exact={true}
                                    />
                                    <PrivateDriverRoute
                                        path='/driver/extra_hour_request'
                                        component={ExtraHourRequests}
                                        exact={true}
                                    />
                                    <PrivateDriverRoute
                                        path='/driver/extra_hour_request/new'
                                        component={ExtraHourRequests}
                                        exact={true}
                                    />
                                </>
                            )}

                            {props.isAuthenticated && (
                                <>
                                    {inlineRoutes.map((route) => (
                                        <PrivateRoute
                                            key={`inline-${route.path}`}
                                            path={route.path}
                                            component={route.ComponentPage}
                                            title={route.title}
                                            exact={true}
                                        />
                                    ))}
                                    {modulesToRender.map((m) => {
                                        if (m.ComponentPage)
                                            return (
                                                <PrivateRoute
                                                    path={m.routePath}
                                                    component={m.ComponentPage}
                                                    title={m.name}
                                                    exact={false}
                                                    key={m.routePath}
                                                />
                                            )
                                    })}
                                </>
                            )}


                            {props.isAssociateAuthenticated && (
                                <>
                                    <PrivateAssociateRoute
                                        path='/sys_company_administrator'
                                        component={ConsoleSuperAdmin}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/modules_dashboard'
                                        component={ModulesDashboard}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/fuel_supply_template'
                                        component={FuelSupplyTemplatePage}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/fuel_supply_template/:id'
                                        component={FuelSupplyTemplateImportScreen}
                                        exact={true}
                                    />

                                    <PrivateAssociateRoute
                                        path='/process_catalog'
                                        component={ProcessCatalog}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/process_catalog/:id'
                                        component={ProcessIndiv}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/associates'
                                        component={AssociatesPage}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/associates/:id'
                                        component={AssociatesIndividualPage}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/wizards_superAdmin'
                                        component={Wizard}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/wizards_superAdmin/:id'
                                        component={WizardIndiv}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/procedures_superAdmin'
                                        component={Procedure}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/procedures_superAdmin/:id'
                                        component={ProcedureIndiv}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/impression_formats_superAdmin'
                                        component={FormatImpression}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/impression_formats_superAdmin/:id'
                                        component={FormatImpressionIndiv}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/communications_superAdmin'
                                        component={Communication}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/communications_superAdmin/:id'
                                        component={CommunicationIndiv}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/partner_billing'
                                        component={PartnerBillingPage}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/partner_billing/:id'
                                        component={PartnerBillingIndividualPage}
                                        exact={true}
                                    />
                                    <PrivateAssociateRoute
                                        path='/query_executor'
                                        component={QueryExecutorPage}
                                        exact={true}
                                    />
                                    {/* <PrivateAssociateRoute
                                    path='/fuel_supply_template'
                                    component={FuelSupplyTemplatePage}
                                    exact={true}
                                />
                                <PrivateAssociateRoute
                                    path='/fuel_supply_template/:id'
                                    component={FuelSupplyTemplateImportScreen}
                                    exact={true}
                                /> */}
                                </>
                            )}
                            {/* <Route path="*" component={ModuleNotFound}/>  */}
                        </Switch>
                    </Suspense>
                        <TourManager />
                    </TabNavigationProvider>
            </BrowserRouter>
            {showChatWidget && <ChatWidget props={props} user={user?.username || client?.email || driver?.user || supplier?.email} />}
        </React.Fragment>
    )
}
const mapDispatchToProps = (dispatch) => ({
    loginWithToken: (parmas) => dispatch(loginWithToken(parmas)),
    clientLoginWithToken: (params) => dispatch(clientLoginWithToken(params)),
    driverLoginWithToken: (params) => dispatch(driverLoginWithToken(params)),
    passengerLoginWithToken: (params) =>
        dispatch(passengerLoginWithToken(params)),
    supplierLoginWithToken: (params) => dispatch(supplierLoginWithToken(params)),
    associateLoginWithToken: (token) => dispatch(associateLoginWithToken(token)),
    passengerLoginWithToken: (params) =>
        dispatch(passengerLoginWithToken(params)),
    setLanguage: (lang) => dispatch(setLanguage(lang)),
})

const mapStateToProps = (state) => ({
    user: state.app.user,
    client: state.app.client,
    driver: state.app.driver,
    supplier: state.app.supplier,
    isAuthenticated: !!state.app.user,
    isClientAuthenticated: !!state.app.client,
    isDriverAuthenticated: !!state.app.driver,
    isPassengerAuthenticated: !!state.app.passenger,
    isSupplierAuthenticated: !!state.app.supplier,
    isAssociateAuthenticated: !!state.app?.superAdmin?.associate,
    isSuperAdminAuthenticated: !!state.app?.superAdmin?.associate?.super_admin,
    lang: state.app?.lang,
})

export default connect(mapStateToProps, mapDispatchToProps)(Routers)
